import React, { useState, ReactNode } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button, Row, Col, Image, Card, Form, Upload, Radio, Divider } from "antd";
import { CardContainer } from "../../components/Card/CardContainer";
import { CameraOutlined, UnorderedListOutlined } from "@ant-design/icons";
import {
  getProductCategory,
  getProductDetail,
  updateProduct,
} from "../../datasource/ProductDatasource";
import { priceFormatter } from "../../utility/Formatter";
import Text from "../../components/Text/Text";
import { BrandEntity } from "../../entities/BrandEntity";
import { LOCATION_FULLNAME_MAPPING } from "../../definitions/location";
import { useEffectOnce } from "react-use";
import BreadCrumb from "../../components/BreadCrumb/BreadCrumb";
import PageTitleNested from "../../components/PageTitle/PageTitleNested";
import { ProductEntity } from "../../entities/PoductEntity";
import styled from "styled-components";
import Input from "../../components/Input/Input";
import color from "../../resource/color";
import TextArea from "../../components/Input/TextArea";
import { ProductCategoryEntity } from "../../entities/ProductCategoryEntity";
import {
  getProductFreebieDetail,
  getProductFreebiePromotionDetail,
  updateProductFreebie,
} from "../../datasource/PromotionDatasource";
import Select from "../../components/Select/Select";
import type { UploadFile } from "antd/es/upload/interface";
import image from "../../resource/image";
import CardSection from "../../components/Card/CardSection";
import { useRecoilValue } from "recoil";
import { roleAtom } from "../../store/RoleAtom";
import { checkPermission } from "../../components/Permission/Permission";
import { useWatch } from "antd/es/form/Form";
import { onInputNumberKeyPress } from "../../utility/validator";
import { resizeFile } from "../../utility/uploadHelper";

const Container = styled.div`
  margin: 32px 0px 10px 0px;
`;

const ProdImage = styled.div`
  width: 100%;
  height: 100%;
  border-radius: 12px;
  color: grey;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const PriceContainer = styled.div`
  padding: 20px;
  background: ${color["background1"]};
  border-radius: 8px;
`;

interface DescProps {
  name: string;
  label: string;
  value?: ReactNode;
  enable?: boolean;
  customInput?: ReactNode;
  withSpace?: boolean;
  customSpan?: number;
  required?: boolean;
}

const ProdFormItem = ({
  name,
  label,
  value,
  enable,
  customInput,
  withSpace,
  customSpan,
  required,
}: DescProps) => {
  return (
    <>
      <Col xl={customSpan ? customSpan : 12} sm={24}>
        <Form.Item label={label} initialValue={value} name={name} rules={[{ required }]}>
          {customInput ? customInput : <Input disabled={!enable} />}
        </Form.Item>
      </Col>
      {withSpace && <Col xl={customSpan ? 24 - customSpan : 12} sm={0} />}
    </>
  );
};

export const DistributionPageEdit: React.FC = (props: any) => {
  const company = JSON.parse(localStorage.getItem("company")!);
  const { pathname } = window.location;
  const pathSplit = pathname.split("/") as Array<string>;
  const isFreebie = pathSplit[2] === "freebies";
  const [form] = Form.useForm();

  const roleData = useRecoilValue(roleAtom);
  const isEdit = checkPermission(["freebieList", "edit"], roleData);
  const navigate = useNavigate();
  const watch = useWatch("productStatus", form);
  const [loading, setLoading] = useState(false);
  const [dataState, setDataState] = useState<any>();
  const [categories, setCategories] = useState<Array<ProductCategoryEntity>>();
  const [file, setFile] = useState<any>();
  const [uploading, setUploading] = useState(false);
  const [fileList, setFileList] = useState<UploadFile[]>();
  const [isRemoved, setRemoved] = useState(false);

  useEffectOnce(() => {
    fetchProduct();
  });

  const fetchProduct = async () => {
    try {
      setLoading(true);
      let data: any = {};
      const id = parseInt(pathSplit[4]);
      if (isFreebie) {
        data = await getProductFreebiePromotionDetail(id);
      } else {
        data = await getProductDetail(parseInt(pathSplit[4]));
      }
      const userProfile = JSON.parse(localStorage.getItem("profile")!);
      const categories = await getProductCategory(company.companyCode);

      setDataState(data);
      setCategories(categories);

      form.setFieldsValue({
        saleUOM:
          data.qtySaleUnit +
          " " +
          (company.companyCode === "ICPL" ? data.baseUOM : data.packingUOM),
        unitPrice:
          priceFormatter(parseFloat(data.unitPrice || "")) +
          "/" +
          (company.companyCode === "ICPL" ? data.baseUOM : data.packingUOM),
        basePrice:
          priceFormatter(parseFloat(data.marketPrice || "")) +
          "/" +
          (data.saleUOMTH || data.saleUOM),
      });

      const url = isFreebie ? data.productFreebies?.productFreebiesImage : data.productImage;
      if (url) {
        setFileList([
          {
            uid: "-1",
            name: "image.png",
            status: "done",
            url,
          },
        ]);
      }
    } catch (e) {
      console.log(e);
    } finally {
      setLoading(false);
    }
  };

  const updateData = async () => {
    const { description, productCategoryId, productStatus, creditTermValue } =
      form.getFieldsValue();
    const data = new FormData();

    if (isFreebie) {
      data.append("productFreebiesId", `${dataState?.productFreebies?.productFreebiesId}`);
      data.append("productFreebiesStatus", `${productStatus}`);
    } else {
      data.append("productId", `${dataState?.productId}`);
      data.append("description", description || "");
      data.append("productCategoryId", productCategoryId);
      data.append("creditTermValue", creditTermValue);
      data.append("productStatus", productStatus);
    }

    if (!isRemoved && dataState?.productImage) {
      data.append("productImage", dataState?.productImage);
    }
    if (!isRemoved && dataState?.productFreebies?.productFreebiesImage) {
      data.append("productFreebiesImage", dataState?.productFreebies?.productFreebiesImage);
    }
    if (file && file.uid !== "-1") {
      const resizedImage = await resizeFile(file, 2);
      data.append("file", resizedImage || file);
    }
    try {
      setUploading(true);
      if (isFreebie) {
        const res = await updateProductFreebie(data);
        navigate(`/freebies/freebies`);
      } else {
        const res = await updateProduct(data);
        console.log("Update product response: ", res);
        navigate(`/PriceListPage/DistributionPage/${dataState?.productId}`);
      }
      //message.success("บันทึกข้อมูลสำเร็จ");
    } catch (e) {
      console.log(e);
    } finally {
      setUploading(false);
    }
  };

  const dataGroup1 = isFreebie
    ? [
        {
          name: "productName",
          label: "ชื่อสินค้า",
          value: dataState?.productFreebies?.productName,
          freebieHide: false,
        },
        {
          name: "productCodeNAV",
          label: "รหัสสินค้า",
          value: isFreebie
            ? dataState?.productFreebies?.productFreebiesCodeNAV
            : dataState?.productCodeNAV,
          freebieHide: false,
        },
        {
          name: "productGroup",
          label: "ชื่อหมวด",
          value: dataState?.productFreebies?.productGroup,
          freebieHide: false,
        },
      ]
    : [
        {
          name: "productCodeNAV",
          label: "รหัสสินค้า",
          value: isFreebie
            ? dataState?.productFreebies?.productFreebiesCodeNAV
            : dataState?.productCodeNAV,
          freebieHide: true,
        },
        {
          name: "productLocation",
          label: "โรงงาน",
          value: LOCATION_FULLNAME_MAPPING[dataState?.productLocation as string],
          freebieHide: true,
        },
        {
          name: "productName",
          label: "ชื่อทางการค้า (Tradename)",
          value: dataState?.productName,
          freebieHide: true,
        },
        {
          name: "commonName",
          label: "ชื่อสามัญ (Common name)",
          value: dataState?.commonName,
          freebieHide: true,
        },
        {
          name: "productBrand",
          label: "Product Brands",
          value: (dataState?.productBrand as BrandEntity)?.productBrandName,
          freebieHide: true,
        },
        {
          name: "productCategoryId",
          label: "หมวดสินค้า (Product Category)",
          value: dataState?.productCategoryId,
          enable: true,
          freebieHide: true,
          required: true,
          customInput: (
            <Select
              data={
                categories?.map((cat: ProductCategoryEntity) => ({
                  key: `${cat.productCategoryId}`,
                  label: cat.productCategoryName,
                  value: `${cat.productCategoryId}`,
                })) || []
              }
              style={{ height: 40 }}
            />
          ),
        },
        {
          name: "creditTermValue",
          label: "กำหนด Credit Term สินค้า",
          value: dataState?.creditTermValue,
          enable: true,
          customInput: (
            <Input
              onKeyDown={onInputNumberKeyPress}
              suffix='วัน'
              style={{ height: 40 }}
              placeholder='ระบุจำนวนวัน'
            />
          ),
        },
      ];

  const dataGroup2 = [
    {
      name: "saleUOM",
      label: "ปริมาณสินค้า / หน่วย",
      // value: baseUOM ? qtySaleUnit + ' ' + baseUOM : '-',
    },
    {
      name: "unitPrice",
      label: "ราคากลาง (Base price)",
      // value: unitPrice ? priceFormatter(parseFloat(unitPrice || "")) + '/' + baseUOM : '-',
    },
  ];

  const dataGroup3 = [
    {
      name: "basePrice",
      label: "ราคากลาง (Base price)",
      // value: marketPrice ? priceFormatter(parseFloat(marketPrice || "")) + '/' + baseUOM : '-',
    },
  ];

  const PageTitle = () => {
    return (
      <PageTitleNested
        title={isFreebie ? "แก้ไขของแถม" : "แก้ไขสินค้า"}
        showBack
        onBack={
          isFreebie
            ? () => navigate(`/${pathSplit[1]}/${pathSplit[2]}`)
            : () => navigate(`/${pathSplit[1]}/${pathSplit[2]}/${dataState?.productId}`)
        }
        customBreadCrumb={
          <BreadCrumb
            data={
              isFreebie
                ? [
                    { text: "รายการของแถม", path: `/${pathSplit[1]}/${pathSplit[2]}` },
                    { text: "แก้ไขของแถม", path: window.location.pathname },
                  ]
                : [
                    { text: "รายชื่อสินค้า", path: `/${pathSplit[1]}/${pathSplit[2]}` },
                    {
                      text: "รายละเอียดสินค้า",
                      path: `/${pathSplit[1]}/${pathSplit[2]}/${
                        isFreebie ? dataState?.productFreebiesId : dataState?.productId
                      }`,
                    },
                    { text: "แก้ไขสินค้า", path: window.location.pathname },
                  ]
            }
          />
        }
      />
    );
  };

  const beforeUpload = (file: any) => {
    const isLt2M = file.size / 1024 / 1024 < 2;
    if (!isLt2M) {
      setFile(undefined);
      setFileList([]);
      alert("Image must smaller than 2MB!");
    }
    // return isLt2M;
    return false;
  };

  return loading ? (
    <div className='container '>
      <Card loading />
    </div>
  ) : (
    <Row justify={"space-between"} gutter={8}>
      <Col span={isFreebie ? 16 : 24}>
        <CardContainer>
          <PageTitle />
          <Row>
            <Text level={5} fontWeight={700}>
              {isFreebie ? "รายละเอียดของแถม" : "รายละเอียดข้อมูลสินค้า"}
            </Text>
          </Row>
          <Form form={form} layout='vertical' onFinish={updateData}>
            <Row style={{ padding: "16px 0px" }}>
              {(company.companyCode === "ICPL" && isFreebie) || company.companyCode !== "ICPL" ? (
                <Form.Item valuePropName='file' name={"productImage"}>
                  {/* <ImgCrop aspect={1}> */}
                  <Upload
                    listType='picture-card'
                    maxCount={1}
                    beforeUpload={beforeUpload}
                    // beforeUpload={() => false}
                    fileList={fileList}
                    //customRequest={() => {}}
                    onChange={({ file, fileList: newFileList }: any) => {
                      const isLt2M = file.size / 1024 / 1024 < 2;
                      if (!isLt2M) {
                        return;
                      }

                      setFile(file);
                      setFileList(newFileList);
                      setRemoved(false);
                      return "success";
                    }}
                    onRemove={() => {
                      setFile(undefined);
                      setFileList([]);
                      setRemoved(true);
                    }}
                  >
                    {!fileList?.length && (
                      <ProdImage>
                        <CameraOutlined />
                      </ProdImage>
                    )}
                  </Upload>
                  <span style={{ color: color.error }}>
                    รูปภาพประกอบสินค้า JPG, PNG. Size of 800*800px 1:1
                  </span>
                  {/* </ImgCrop> */}
                </Form.Item>
              ) : (
                <>
                  {dataState?.productImage ? (
                    <Container>
                      <Image
                        src={dataState?.productImage}
                        style={{
                          width: "136px",
                          height: "136px",
                          objectFit: "contain",
                        }}
                      />
                    </Container>
                  ) : (
                    <Container>
                      <Image
                        src={image.product_no_image}
                        style={{
                          width: "136px",
                          height: "136px",
                          objectFit: "contain",
                        }}
                      />
                    </Container>
                  )}
                </>
              )}
            </Row>
            <Row gutter={24}>
              {dataGroup1
                ?.filter((e: any) => !isFreebie || !e.freebieHide)
                .map((d, i) => <ProdFormItem {...d} customSpan={isFreebie ? 24 : 12} key={i} />)}
              {isFreebie && (
                <>
                  <Col xl={12} sm={12}>
                    <Form.Item label='ขนาด'>
                      <Input value='-' disabled />
                    </Form.Item>
                  </Col>
                  <Col xl={12} sm={12}>
                    <Form.Item label='หน่วย'>
                      <Input
                        value={
                          dataState?.productFreebies?.baseUnitOfMeaTh
                            ? dataState?.productFreebies?.baseUnitOfMeaTh
                            : dataState?.productFreebies?.baseUnitOfMeaEn
                        }
                        disabled
                      />
                    </Form.Item>
                  </Col>
                </>
              )}
            </Row>
            {!isFreebie && (
              <>
                <PriceContainer>
                  <Text color='primary' fontWeight={700}>
                    UNIT SIZE
                  </Text>
                  <Row gutter={24}>
                    {dataGroup2.map((d, i) => (
                      <ProdFormItem {...d} key={i} />
                    ))}
                  </Row>
                  <Text color='primary' fontWeight={700}>
                    PACKAGE SIZE
                  </Text>
                  <Row gutter={24}>
                    {dataGroup3.map((d, i) => (
                      <ProdFormItem {...d} key={i} />
                    ))}
                  </Row>
                </PriceContainer>
                <br />
              </>
            )}
            <Row>
              <Col span={24}>
                <Form.Item
                  label={isFreebie ? "คุณสมบัติ" : "คุณสมบัติและประโยชน์"}
                  name='description'
                  initialValue={
                    isFreebie ? dataState?.productFreebies?.description : dataState?.description
                  }
                >
                  <TextArea disabled={isFreebie} />
                </Form.Item>
              </Col>
            </Row>
            <br />
            <Form.Item
              label={"สถานะสินค้า"}
              name='productStatus'
              initialValue={
                isFreebie
                  ? dataState?.productFreebies?.productFreebiesStatus
                  : dataState?.productStatus
              }
            >
              <Radio.Group
                disabled={
                  company.companyCode === "ICPI" || company.companyCode === "ICPF"
                    ? false
                    : !isFreebie
                }
              >
                <Radio value={"ACTIVE"}>ใช้งาน</Radio>
                <Radio value={"INACTIVE"}>ปิดการใช้งาน</Radio>
                {!isFreebie && <Radio value={"HOLD"}>ปิดชั่วคราว</Radio>}
              </Radio.Group>
            </Form.Item>
            <Divider />
            {!isFreebie ? (
              <Row align='middle' justify='end'>
                <Button
                  type='primary'
                  htmlType='submit'
                  size='large'
                  style={{ width: 136 }}
                  loading={uploading}
                >
                  บันทึก
                </Button>
              </Row>
            ) : (
              <>
                {isEdit && (
                  <Row align='middle' justify='end'>
                    <Button
                      type='primary'
                      htmlType='submit'
                      size='large'
                      style={{ width: 136 }}
                      loading={uploading}
                    >
                      บันทึก
                    </Button>
                  </Row>
                )}
              </>
            )}
          </Form>
        </CardContainer>
      </Col>
      {isFreebie && (
        <Col span={8}>
          <CardSection title='โปรโมชันที่ของแถมเข้าร่วม' bgColor='#1B4586' textColor='white'>
            <CardContainer style={{ borderRadius: "5px", height: "500px" }}>
              {dataState?.promotionOfProduct?.length || 0 > 0 ? (
                <>
                  <Text>รายการทั้งหมด {dataState?.promotionOfProduct?.length} รายการ</Text>
                  <Divider />
                  {dataState?.promotionOfProduct?.map((x: any) => (
                    <>
                      <Row justify={"space-between"} key={x.promotionId}>
                        <Col span={3}>
                          <Image src={x.promotionImageSecond || image.emptyPromotion} width={70} />
                        </Col>
                        <Col span={13}>
                          <Text level={6}>{x.promotionName}</Text>
                          <br />
                          <Text color='Text3' level={6}>
                            {x.promotionCode}
                          </Text>
                        </Col>
                        <Col span={2}>
                          <Link
                            to={`/PromotionPage/promotion/detail/${x.promotionId}`}
                            rel='noopener noreferrer'
                            target='_blank'
                          >
                            <div className='btn btn-icon btn-light btn-hover-primary btn-sm'>
                              <span className='svg-icon svg-icon-primary svg-icon-2x'>
                                <UnorderedListOutlined style={{ color: color["primary"] }} />
                              </span>
                            </div>
                          </Link>
                        </Col>
                      </Row>
                      <br />
                    </>
                  ))}
                </>
              ) : (
                <>
                  <Text>รายการทั้งหมด {dataState?.promotionOfProduct?.length} รายการ</Text>
                  <Divider />
                  <div style={{ display: "flex", justifyContent: "center" }}>
                    <Image src={image.emptyProFreebie} preview={false} />
                  </div>
                  <div style={{ display: "flex", justifyContent: "center" }}>
                    <Text align='center' color='Text3'>
                      ไม่มีโปรโมชันที่ของแถมเข้าร่วม
                    </Text>
                  </div>
                </>
              )}
            </CardContainer>
          </CardSection>
        </Col>
      )}
    </Row>
  );
};
